// src/components/core/header.ts

import {
	createElement,
	getElementByIdOrThrow,
	getElementByIdSafe,
} from "../../utils/domHelpers";
import { renderJackpot } from "../common/jackpotDisplay"; // Import jackpot rendering
import { HeaderData, Jackpot } from "../../types/api";
import { formatUTCDateTime } from "../../utils/date";

/**
 * Helper function to calculate header padding based on navigation state
 * @returns The appropriate padding-top value in pixels
 */
function getHeaderPaddingTop(): string {
    const navEl = document.querySelector(".navbar") as HTMLElement | null;
    const mobileLeaderboard = document.getElementById("mobile-leaderboard-row") as HTMLElement | null;
    const desktopLeaderboard = document.getElementById("desktop-leaderboard-row") as HTMLElement | null;
    const isMobile = window.innerWidth < 992;

    const basePadding = navEl?.offsetHeight || (isMobile ? 60 : 80);
    const leaderboardPad = (isMobile ? mobileLeaderboard?.offsetHeight : desktopLeaderboard?.offsetHeight) || 0;

    return `${basePadding + leaderboardPad}px`;
}

/**
 * Recalculate and apply header padding for all visible header text containers.
 * Useful after SPA navigations when navbar/leaderboard rows are added/removed.
 * Entry page containers use 20px padding instead of the calculated value.
 */
export function updateHeaderPaddingAll(): void {
    try {
        const containers = document.querySelectorAll<HTMLElement>(".headingContentText");
        const pad = getHeaderPaddingTop();
        containers.forEach((el) => {
            // Entry pages use 20px padding instead of calculated padding
            if (el.getAttribute("data-entry-page") === "true") {
                (el.style as any).paddingTop = "20px";
            } else {
                (el.style as any).paddingTop = pad;
            }
        });
    } catch (_) {
        // ignore
    }
}


/**
 * Helper function to create wave decoration
 * @param header - Header element to append wave to
 * @param showWave - Whether to show the wave
 * @param wavePosition - Position of the wave (top or bottom)
 * @param waveStyle - The visual style of the wave ('gold-swirl' or 'black')
 */
function createWaveDecoration(
	header: HTMLElement,
	showWave: boolean,
	wavePosition: "top" | "bottom",
	waveStyle: "gold-swirl" | "black" = "gold-swirl"
): void {
	if (showWave) {
		if (wavePosition === "bottom") {
			header.classList.add("has-wave-bottom");
		}

		const isBlackWave = waveStyle === "black";
		const waveImage = isBlackWave
			? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 120'%3E%3Cpath fill='%23000000' d='M0,64L480,96L960,64L1440,96L1440,120L0,120Z'%3E%3C/path%3E%3C/svg%3E")`
			: "url(/static/img/black-swirl.png)";

		createElement(
			"div",
			{
				className: `header-wave wave-${wavePosition}`,
				style: {
					position: "absolute",
					left: "0",
					width: "100%",
					height: "120px",
					backgroundImage: waveImage,
					backgroundSize: "cover",
					backgroundPosition: isBlackWave ? "bottom" : "center",
					zIndex: "1",
					...(wavePosition === "bottom"
						? { bottom: "0" }
						: { top: "0", transform: "rotate(180deg)" }),
				},
			},
			header
		);

		// Add CSS media query for mobile wave height
		const style = document.createElement("style");
		style.textContent = `
			.has-wave-bottom .headingContentText {
				padding-bottom: 200px;
			}
            @media (max-width: 768px) {
                .header-wave {
                    height: 80px !important;
                }
				.has-wave-bottom .headingContentText {
					padding-bottom: 80px;
                }
            }
        `;
		document.head.appendChild(style);
	}
}

/**
 * Creates a generic page header with a background image, overlay, and title.
 * Optionally renders jackpot information.
 *
 * @param holdingContainerId - The ID of the container element to append the header to.
 * @param data - Data object potentially containing image URL and jackpot info.
 * @param headerText - The main text to display in the header.
 * @param renderJackpotFlag - Whether to render the jackpot display.
 * @param countJackpot - Whether to animate the jackpot count-up.
 * @param backgroundImage - Optional custom background image URL to override default/data image.
 * @param buttonText - Optional text for button below headline.
 * @param buttonUrl - Optional URL for the button link.
 * @param buttonClasses - Optional CSS classes for the button (defaults to Bootstrap button styles).
 * @param showWave - Whether to show a wave decoration at the bottom of the header.
 * @param wavePosition - Position of the wave (default: "bottom").
 * @param waveStyle - The visual style of the wave ('gold-swirl' or 'black').
 */
export function createGenericPageHeader(
	holdingContainerId: string,
	data: Partial<HeaderData>, // Use Partial as not all fields might be present
	headerText: string = "Header Text",
	renderJackpotFlag: boolean = false,
	countJackpot: boolean = true,
	backgroundImage?: string, // New parameter for custom background image
	buttonText?: string, // New parameter for button text
	buttonUrl?: string, // New parameter for button URL
	buttonClasses: string = "btn btn-primary btn-lg mt-3 px-5", // Default button styling
	showWave: boolean = false, // Whether to show wave decoration
	wavePosition: "top" | "bottom" = "bottom", // Wave position
	subHeaderText?: string, // Optional: Text to display below the main header
	childElement?: HTMLElement, // Optional: Element to append inside the text container
	waveStyle: "gold-swirl" | "black" = "gold-swirl"
): void {
	const holdingContainer = getElementByIdOrThrow(holdingContainerId);
	// Clear previous header content if necessary
	// holdingContainer.innerHTML = ''; // Optional: depends on usage pattern

	const header = createElement("header", {
		id: "headingContent", // Consistent ID from original JS
		className: "text-white text-center py-5", // Base classes
		style: {
			position: "relative",
			minHeight: showWave ? "450px !important" : "300px !important", // Much taller when wave is shown
			height: showWave ? "450px !important" : "auto !important", // Force height override with !important
		},
	});

	// Background Image - Use the new parameter, falling back to data.image or default
	createElement(
		"img",
		{
			className: "w-100 h-100",
			attributes: {
				// Priority: custom background > data.image > default image
				// src: backgroundImage || data.image || '/static/img/generic-hole-image.jpg',
				src: backgroundImage || data.image || "/static/img/golf-course.jpg",
				alt: "Header background",
			},
			style: {
				objectFit: "cover",
				position: "absolute",
				top: "0",
				left: "0",
				zIndex: "-2",
				boxShadow: "inset 0px -30px 20px rgba(0, 0, 0, 0.7)", // Added shadow from admin heading
			},
		},
		header
	);

	// Overlay
	createElement(
		"div",
		{
			id: "headerOverlay", // Give overlay an ID if needed
			style: {
				position: "absolute",
				top: "0",
				left: "0",
				width: "100%",
				height: "100%",
				backgroundColor: "rgba(0, 0, 0, 0.55)", // Overlay color
				zIndex: "0",
			},
		},
		header
	);

	// Text Container - Adjust positioning based on wave
	const textContainer = createElement("div", {
		className:
			"container d-flex flex-column justify-content-center align-items-center headingContentText h-100", // Ensure vertical centering if header has fixed height
		style: {
			position: "relative",
			zIndex: "1",
			minHeight: "inherit",
			height: "auto !important", // Override any fixed height from CSS
			paddingTop: getHeaderPaddingTop(), // Dynamic padding to account for navbar + leaderboard
			// Add padding to avoid wave overlap
			...(showWave && wavePosition === "top" ? { paddingTop: "240px" } : {}), // 80px nav + 160px wave
		},
	});

	// Title
	createElement(
		"h1",
		{
			className: "pt-5 px-4 pb-2 fw-bold", // Adjusted padding
			style: { fontSize: "2rem" }, // Larger font size for emphasis
			innerHTML: headerText,
		},
		textContainer
	);

	// Optional Sub-header text
	if (subHeaderText) {
		createElement(
			"p",
			{
				className: "lead mb-4", // Bootstrap 'lead' class for emphasis
				innerHTML: subHeaderText, // Use innerHTML to allow for simple formatting
				style: {
					//textShadow: "0 1px 3px rgba(0,0,0,0.6)",
				},
			},
			textContainer
		);
	}

	// Optional Button below headline
	if (buttonText && buttonUrl) {
		createElement(
			"a",
			{
				className: buttonClasses,
				attributes: {
					href: buttonUrl,
					role: "button",
				},
				textContent: buttonText,
			},
			textContainer
		);
	}

	// Optional Hole Info (from createAdminHeading logic)
	if (data.hole_number) {
		const holeInfo = createElement("h6", {
			className: "course-sub-header d-inline m-2",
			textContent: ` Hole ${data.hole_number}`,
		});
		const holeIcon = createElement("i", {
			className: "fa-solid fa-location-pin m-1",
		});
		holeInfo.prepend(holeIcon);
		textContainer.appendChild(holeInfo);
	}

	// Append text container to header, then header to holding container
	header.appendChild(textContainer);
	holdingContainer.appendChild(header);

	// Add optional wave decoration using helper function
	createWaveDecoration(header, showWave, wavePosition, waveStyle);

	// Render Jackpot if requested and data is available
	if (renderJackpotFlag && data.jackpot) {
		const jackpotDiv = createElement("div", {
			id: "jackpotDiv", // Consistent ID
			className: "p-2 pt-3", // Padding from original JS
		});
		// Prepend jackpot within the holding container but after the main header content usually
		holdingContainer.appendChild(jackpotDiv); // Or header.appendChild(jackpotDiv) depending on desired layout
		renderJackpot(jackpotDiv, data.jackpot, countJackpot);
	} else if (renderJackpotFlag) {
		console.warn("renderJackpotFlag is true, but jackpot data is missing.");
	}
}

/**
 * Creates an admin-style header with a background image, overlay, course name, and optional hole info.
 * Optionally renders jackpot information after the header.
 * Typically used for admin pages, confirmation screens, and purchase flows.
 *
 * @param holdingContainerId - The ID of the container element to append the header to.
 * @param data - Data containing course name, image, and optional hole/jackpot info.
 * @param countJackpot - Whether to animate the jackpot count-up.
 * @param showJackpot - Whether to render the jackpot display.
 * @param renderHoleInfo - Whether to include hole information in the header.
 * @param backgroundImage - Optional custom background image URL to override data.image.
 * @param buttonText - Optional text for button below headline.
 * @param buttonUrl - Optional URL for the button link.
 * @param buttonClasses - Optional CSS classes for the button (defaults to Bootstrap button styles).
 * @param showWave - Whether to show a wave decoration at the bottom of the header.
 * @param wavePosition - Position of the wave (default: "bottom").
 * @param waveStyle - The visual style of the wave ('gold-swirl' or 'black').
 */
export function createAdminHeading(
	holdingContainerId: string,
	data: HeaderData,
	countJackpot: boolean = true,
	showJackpot: boolean = true,
	renderHoleInfo: boolean = true,
	backgroundImage?: string, // New parameter for custom background image
	buttonText?: string, // New parameter for button text
	buttonUrl?: string, // New parameter for button URL
	childElement?: HTMLElement,
	buttonClasses: string = "btn btn-secondary btn-lg mt-3", // Default button styling
	showWave: boolean = false, // Whether to show wave decoration
	wavePosition: "top" | "bottom" = "bottom", // Wave position
	waveStyle: "gold-swirl" | "black" = "gold-swirl"
): void {
	const holdingContainer = getElementByIdOrThrow(holdingContainerId);

	// Create the header element
	const header = createElement("header", {
		id: "headingContent",
		className: "text-white text-center py-5",
		style: {
			position: "relative",
			minHeight: showWave ? "450px !important" : "300px !important", // Much taller when wave is shown
			height: showWave ? "450px !important" : "auto !important", // Force height override with !important
		},
	});

	// Background Image - Use the custom image if provided, fallback to data.image
	createElement(
		"img",
		{
			className: "w-100 h-100",
			attributes: {
				src: backgroundImage || data.image,
				alt: "Header background",
			},
			style: {
				objectFit: "cover",
				position: "absolute",
				top: "0",
				left: "0",
				zIndex: "-2",
				boxShadow: "inset 0px -30px 20px rgba(0, 0, 0, 0.7)",
			},
		},
		header
	);

	// Overlay
	createElement(
		"div",
		{
			style: {
				position: "absolute",
				top: "0",
				left: "0",
				width: "100%",
				height: "100%",
				backgroundColor: "rgba(0, 0, 0, 0.35)",
				zIndex: "0",
			},
		},
		header
	);

	// Text Container - Adjust positioning based on wave
	const textContainer = createElement("div", {
		className:
			"container d-flex flex-column justify-content-center align-items-center headingContentText",
		style: {
			position: "relative",
			zIndex: "1",
			height: "auto !important", // Override any fixed height from CSS
			paddingTop: getHeaderPaddingTop(), // Add top padding to clear fixed navigation
			// Add padding to avoid wave overlap
			...(showWave && wavePosition === "top" ? { paddingTop: "240px" } : {}), // 80px nav + 160px wave
		},
	});

	// Title (Course Name)
	createElement(
		"h1",
		{
			className: "p-2",
			textContent: data.course.name,
		},
		textContainer
	);

	// Optional child element (for stats, etc.)
	if (childElement) {
		textContainer.appendChild(childElement);
	}

	// Optional Button below headline
	if (buttonText && buttonUrl) {
		createElement(
			"a",
			{
				className: buttonClasses,
				attributes: {
					href: buttonUrl,
					role: "button",
				},
				textContent: buttonText,
			},
			textContainer
		);
	}

	// Optional Hole Info
	if (renderHoleInfo && data.hole_number) {
		// Three columns on desktop: Hole, Date, Views
		const detailsRow = createElement(
			"div",
			{ className: "row w-100 justify-content-center align-items-center g-0" },
			textContainer
		);

		const holeCol = createElement("div", { className: "col-12 col-md-3" }, detailsRow);
		const dateCol = createElement("div", { className: "col-12 col-md-3" }, detailsRow);
		const viewsCol = createElement("div", { className: "col-12 col-md-3" }, detailsRow);

		const holeInfo = createElement("h6", {
			className: "course-sub-header d-inline m-2 m-md-0",
			textContent: ` Hole ${data.hole_number} , ${data.entry?.tee}`,
		}, holeCol);

		if (renderHoleInfo) {
			const holeIcon = createElement("i", { className: "fa-solid fa-flag m-1" });
			holeInfo.prepend(holeIcon);
		}

		createElement(
			"h6",
			{
				className: "text-white m-2 m-md-0",
				innerHTML: `<strong>Date:</strong> ${
					data.entry?.created_at ? formatUTCDateTime(data.entry?.created_at) : "Unknown"
				}`,
			},
			dateCol
		);

		createElement(
			"p",
			{
				className: "mb-0 mt-2 mt-md-0",
				id: "recording-views",
				innerHTML: `<i class=\"fa-solid fa-eye\"></i> ${data.entry?.views} Views`,
			},
			viewsCol
		);

		// Add a container for the desktop share button
		const desktopShareContainer = createElement(
			"div",
			{
				id: "desktop-share-container",
				className: "mt-4 mb-2 d-none d-sm-block",
			},
			textContainer
		);
	}

	// Append text container to header
	header.appendChild(textContainer);

	// Append header to holding container
	holdingContainer.appendChild(header);

	// Add optional wave decoration using helper function
	createWaveDecoration(header, showWave, wavePosition, waveStyle);

	// Add Jackpot after the header if requested
	if (showJackpot && data.jackpot) {
		const jackpotDiv = createElement("div", {
			id: "jackpotDiv",
			className: "p-2 pt-3",
		});
		holdingContainer.appendChild(jackpotDiv);
		renderJackpot(jackpotDiv, data.jackpot, countJackpot);
	}
}

export function generateDashboardHeading(
	data: any,
	pageName: string,
	parentContainerId: string | null = null
): void {
	// Create the header element
	const header = document.createElement("header");
	header.id = "headingContent";
	// Adjust padding and remove the extra class
	header.className = "text-white text-center pt-5 pb-4"; // Increased top padding, adjusted bottom
	header.style.position = "relative";
	//header.style.height = "219px";

	// Create the img element
	const img = document.createElement("img");
	img.sizes = "(max-width: 1089px) 100vw, 1089px";
	img.srcset = `/static/img/generic-hole-image_spbaw0_c_scale,w_200.jpg 200w,
/static/img/generic-hole-image_spbaw0_c_scale,w_557.jpg 557w,
/static/img/generic-hole-image_spbaw0_c_scale,w_819.jpg 819w,
/static/img/generic-hole-image_spbaw0_c_scale,w_1035.jpg 1035w,
/static/img/generic-hole-image_spbaw0_c_scale,w_1222.jpg 1222w,
/static/img/generic-hole-image_spbaw0_c_scale,w_1400.jpg 1400w`;
	img.src = "/static/img/generic-hole-image.jpg";
	img.alt = "Hero Image";
	img.className = "w-100 h-100";
	img.style.objectFit = "cover";
	img.style.position = "absolute";
	img.style.top = "0";
	img.style.left = "0";
	img.style.zIndex = "-1";

	// Create the overlay div
	const overlay = document.createElement("div");
	overlay.style.position = "absolute";
	overlay.style.top = "0";
	overlay.style.left = "0";
	overlay.style.width = "100%";
	overlay.style.height = "100%";
	overlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
	overlay.style.zIndex = "0";

	// Create the container div
	const container = document.createElement("div");
	container.className =
		"container d-flex flex-column justify-content-center align-items-center headingContentText dashboardHeading";
	container.style.position = "relative";
	container.style.zIndex = "1";
	container.style.minHeight = "inherit";
	container.style.height = "auto !important"; // Override any fixed height from CSS
	container.style.paddingTop = getHeaderPaddingTop(); // Add top padding to clear fixed navigation

	// Create the h1 element for the title
	const title = document.createElement("h1");
	title.textContent = pageName;

	// Create the p element for the user info
	const userInfo = document.createElement("p");

	// Create the i element for the icon
	const icon = document.createElement("i");
	icon.className = "fa-solid fa-user me-2";

	// Create the span element for the user name
	const userName = document.createElement("span");
	userName.textContent = data.full_name;

	// Append icon and userName to the userInfo paragraph
	userInfo.appendChild(icon);
	userInfo.appendChild(userName);

	// Append the title and userInfo to the container div
	container.appendChild(title);
	container.appendChild(userInfo);

	// Append the img, overlay, and container to the header element
	header.appendChild(img);
	header.appendChild(overlay);
	header.appendChild(container);

	const parentElement = parentContainerId
		? document.getElementById(parentContainerId)
		: document.body;
	if (parentElement) {
		parentElement.appendChild(header);
	}
}

// src/components/core/breadcrumbs.ts

export function generateBreadCrumbs(
	page: string,
	secondPage: string | null = null,
	secondPageUrl: string | null = null,
	parentContainerId: string | null = null
): void {
	const parentContainer = document.createElement("div");
	parentContainer.setAttribute("id", "breadcrumbs-generated-container");
	parentContainer.setAttribute("class", "container");

	const breadcrumbs = document.createElement("nav");
	breadcrumbs.setAttribute("aria-label", "breadcrumb");
	breadcrumbs.setAttribute("id", "breadcrumbs");
	breadcrumbs.setAttribute("class", "p-2 pt-3");

	const ol = document.createElement("ol");
	ol.setAttribute("class", "breadcrumb");
	// REMOVED: ol.style.backgroundColor = "#fff";

	const li = document.createElement("li");
	li.setAttribute("class", "breadcrumb-item");
	// Changed icon color to be light
	li.innerHTML =
		'<a href="/dashboard"><i class="fa-solid fa-home text-light"></a>';
	ol.appendChild(li);

	const li2 = document.createElement("li");
	li2.setAttribute("class", "breadcrumb-item");

	if (!secondPage) {
		li2.setAttribute("aria-current", "page");
		li2.classList.add("active", "text-light"); // Make active text light
		li2.textContent = page;
	} else {
		// Changed link color to be light
		li2.innerHTML = `<a href="/${secondPageUrl}" class="text-light" style="text-decoration:none">${page}</a>`;
	}
	ol.appendChild(li2);

	if (secondPage) {
		const li3 = document.createElement("li");
		li3.setAttribute("class", "breadcrumb-item active text-light"); // Make active text light
		li3.setAttribute("aria-current", "page");
		li3.textContent = secondPage;
		ol.appendChild(li3);
	}

	breadcrumbs.appendChild(ol);

	parentContainer.appendChild(breadcrumbs);

	if (parentContainerId) {
		const parentElement = getElementByIdSafe(parentContainerId);

		if (parentElement) {
			parentElement.appendChild(parentContainer);
		} else {
			document.body.appendChild(parentContainer);
		}
	} else {
		document.body.appendChild(parentContainer);
	}
}
